import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "../../styles/components/product/FavoritesList.module.css";
import getProductImageUrl from "../../utils/imageUtils";
import { FaEye, FaTrashAlt, FaHeart, FaTag } from "react-icons/fa";
import { useToast } from "../../contexts/ToastContext";
import capitalizeFirst from "../../utils/capitalizeFirst";
import FavoritesListSkeleton from "./FavoritesListSkeleton";
import api from "../../utils/api/axiosInstance";

const FavoritesList = ({
  favoriteIds = [],
  loadingIds = false,
  isEdit,
  selectedFavorites,
  setSelectedFavorites,
  refresh,
}) => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [favorites, setFavorites] = useState([]);
  const [skip, setSkip] = useState(0);
  const [limit] = useState(12);
  const [hasMore, setHasMore] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (loadingIds) {
        return;
      }

      const ids = Array.isArray(favoriteIds) ? favoriteIds : [];

      try {
        setLoadingProducts(true);
        setSkip(0);

        if (ids.length === 0) {
          setFavorites([]);
          setHasMore(false);
          return;
        }

        const firstBatch = ids.slice(0, limit);
        const detailsRes = await api.post("/products/bulk", {
          ids: firstBatch,
        });
        const items = Array.isArray(detailsRes.data) ? detailsRes.data : [];

        setFavorites(items);
        setSkip(limit);
        setHasMore(ids.length > limit);
      } catch (err) {
        console.error(
          "Failed to load favorites:",
          err.response?.data || err.message,
        );
        setFavorites([]);
        setHasMore(false);
      } finally {
        setLoadingProducts(false);
      }
    };

    load();
  }, [favoriteIds, limit, refresh, loadingIds]);

  const handleRemove = async (id) => {
    try {
      await api.delete("/users/me/favorites", {
        data: { productId: id },
      });

      const updated = favorites.filter((p) => p._id !== id);
      setFavorites(updated);

      const remainingIds = favoriteIds.filter((x) => x !== id);
      if (updated.length < favorites.length) {
        setHasMore(remainingIds.length > updated.length);
      }

    } catch (err) {
      console.error(
        "Failed to remove favorite:",
        err.response?.data || err.message,
      );
      showToast({ message: "Error removing favorite", type: "error" });
    }
  };

  const handleShowMore = async () => {
    try {
      const nextBatch = favoriteIds.slice(skip, skip + limit);
      if (nextBatch.length === 0) {
        setHasMore(false);
        return;
      }

      const detailsRes = await api.post("/products/bulk", { ids: nextBatch });
      const items = Array.isArray(detailsRes.data) ? detailsRes.data : [];

      setFavorites((prev) => [...prev, ...items]);
      const nextSkip = skip + limit;
      setSkip(nextSkip);
      if (nextSkip >= favoriteIds.length) setHasMore(false);
    } catch (err) {
      console.error(
        "Failed to load more favorites:",
        err.response?.data || err.message,
      );
    }
  };

  const getProductName = (product) =>
    product.name
      ? capitalizeFirst(product.name)
      : `${
          capitalizeFirst(product.brand?.name) || capitalizeFirst(product.brand)
        } ${capitalizeFirst(product.model)}`;

  if (loadingIds || loadingProducts) {
    return (
      <div className={styles.favoritesGrid}>
        {Array.from({ length: 8 }).map((_, i) => (
          <FavoritesListSkeleton key={`fav-skel-${i}`} />
        ))}
      </div>
    );
  }

  if (!favorites.length) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyIcon}>
          <FaHeart />
        </div>
        <h3 className={styles.emptyTitle}>No favorites yet</h3>
        <p className={styles.emptyMessage}>
          Start saving products you love! Click the heart icon on any product to
          add it to your favorites.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className={styles.favoritesGrid}>
        {favorites.map((product) => (
          <div
            key={product._id}
            className={`${styles.favoriteCard} ${
              selectedFavorites.includes(product._id) ? styles.selected : ""
            }`}
          >
            <span className={styles.checkbox}>
              {isEdit && (
                <input
                  type="checkbox"
                  checked={selectedFavorites.includes(product._id)}
                  aria-label={`Select ${getProductName(product)}`}
                  onClick={(event) => event.stopPropagation()}
                  onChange={(e) => {
                    const { checked } = e.target;
                    setSelectedFavorites((prev) =>
                      checked
                        ? [...new Set([...prev, product._id])]
                        : prev.filter((pid) => pid !== product._id),
                    );
                  }}
                />
              )}
            </span>

            {product.active === false && (
              <div className="inactiveSign">
                This product is no longer active
              </div>
            )}

            <div
              className={styles.favoriteCardLink}
              onClick={() => {
                if (isEdit) {
                  const checked = !selectedFavorites.includes(product._id);
                  setSelectedFavorites((prev) =>
                    checked
                      ? [...new Set([...prev, product._id])]
                      : prev.filter((pid) => pid !== product._id),
                  );
                  return;
                }
                navigate(`/product/${product._id}`);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  if (isEdit) {
                    const checked = !selectedFavorites.includes(product._id);
                    setSelectedFavorites((prev) =>
                      checked
                        ? [...new Set([...prev, product._id])]
                        : prev.filter((pid) => pid !== product._id),
                    );
                    return;
                  }
                  navigate(`/product/${product._id}`);
                }
              }}
              role={isEdit ? "button" : "link"}
              aria-pressed={isEdit ? selectedFavorites.includes(product._id) : undefined}
              tabIndex={0}
            >
              <img
                src={getProductImageUrl(product.images?.[0])}
                alt={getProductName(product)}
                className={styles.favoriteImage}
              />

              <div className={styles.favoriteBody}>
                <h3 className={styles.favoriteName}>{getProductName(product)}</h3>
                <span className={styles.favoriteCategory}>
                  <FaTag aria-hidden />
                  {capitalizeFirst(product.category?.name) ||
                    capitalizeFirst(product.category)}
                </span>
                <p className={styles.favoritePrice}>
                  ${product?.price?.toLocaleString()}
                </p>
              </div>
            </div>

            <div className={styles.favoriteActions}>
              <button
                type="button"
                className={styles.favoriteActionBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/product/${product._id}`);
                }}
                title="View Product"
              >
                <FaEye size={16} />
              </button>
              {!isEdit && (
                <button
                  type="button"
                  className={`${styles.favoriteActionBtn} ${styles.favoriteActionBtnDanger}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(product._id);
                  }}
                  title="Remove from Favorites"
                >
                  <FaTrashAlt size={16} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {hasMore && (
        <div
          className="showMoreContainer"
          style={{ textAlign: "center", marginTop: "var(--spacing-xl)" }}
        >
          <button type="button" className="buttonShowMore" onClick={handleShowMore}>
            Show More
          </button>
        </div>
      )}
    </div>
  );
};

export default FavoritesList;
